import { BillingPeriodType } from '@constants/billingPeriods'
import { CurrencyType, PLN } from '@constants/currencies'
import {
  PROMOTION_3M_PLANS,
  PROMOTION_12M_PLANS,
  PROMOTION_FOR_FREE_PLAN_PLANS,
  promotion3MDiscountedPricesBrutto,
  promotion12MDiscountedPricesBrutto,
  promotionForFreePlanDiscountedPricesBrutto
} from '@constants/promotions'
import { formatDateUTCToDisplay } from '@helpers/date'
import { convertPriceToNetto, formatPrice } from '@helpers/payment'
import { Icon, Paragraph, Spacer } from '@landingi/landingi-ui-kit'
import styles from '@pages/Pricing/routes/AccountPackage/components/PlanPanels/components/SummarySubscriptionModal/SummarySubscriptionModal.module.scss'
import { Discount } from '@services/discounts'
import { Spreader } from '@ui-kit'
import { TFunction } from 'i18next'
import { Fragment } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Column, Row } from 'simple-flexbox'

const getPriceEquation = ({
  t,
  priceInMonths,
  oldPriceInMonths,
  currency,
  billingPeriod,
  discount
}: {
  t: TFunction<'translation', undefined>
  priceInMonths: string
  oldPriceInMonths?: string
  currency: CurrencyType
  billingPeriod: BillingPeriodType
  discount?: Discount | null
}) => {
  const equationWithoutDiscount = (
    <Fragment>
      {oldPriceInMonths && (
        <Fragment>
          <Paragraph
            className={styles['price-in-months-paragraph']}
            line={20}
            size={12}
            padding='none'
            decoration='line-through'
            color='accent-3'
          >
            {`${oldPriceInMonths} ${currency}`}
          </Paragraph>{' '}
        </Fragment>
      )}

      {t('registration.flow.credit.card.step.equation', {
        price: priceInMonths,
        currency,
        count: billingPeriod
      })}
    </Fragment>
  )

  const equationWithDiscount = `${t(
    'registration.flow.credit.card.step.equation',
    {
      price: priceInMonths,
      currency,
      count: billingPeriod
    }
  )} - ${discount} ${currency}`

  const equation = discount ? equationWithDiscount : equationWithoutDiscount

  const equationForPLN = <Fragment>({equation}) + 23% VAT</Fragment>

  return currency === PLN ? equationForPLN : equation
}

export const TotalDueTodayFree = ({
  price,
  currency
}: {
  price: number
  currency: CurrencyType
}) => {
  const { t } = useTranslation()

  return (
    <div className={styles['summary-subscription-modal__row']}>
      <div className={styles['summary-subscription-modal__icon']}>
        <Icon icon='icon-credit-card-outline' size={14} />
      </div>
      <div>
        <Paragraph weight={700} line={20} size={16} padding='none'>
          <Trans i18nKey='change.plan.page.total.due.today' />
        </Paragraph>
      </div>

      <div>
        <Paragraph line={20} size={16} padding='none'>
          {formatPrice(price)} {currency}{' '}
          {currency === PLN && t('registration.flow.credit.card.step.gross')}
        </Paragraph>
      </div>
    </div>
  )
}

export const TotalDueTodayFreePromotion3M = ({
  price,
  currency,
  planRecordKey
}: {
  price: number
  currency: CurrencyType
  planRecordKey: string
}) => {
  const { t } = useTranslation()

  const promotion3MPrice =
    promotion3MDiscountedPricesBrutto[
      planRecordKey as (typeof PROMOTION_3M_PLANS)[number]
    ][currency]

  return (
    <div className={styles['summary-subscription-modal__row']}>
      <div className={styles['summary-subscription-modal__icon']}>
        <Icon icon='icon-credit-card-outline' size={14} />
      </div>
      <div>
        <Paragraph weight={700} line={20} size={16} padding='none'>
          <Trans i18nKey='change.plan.page.total.due.today' />
        </Paragraph>
      </div>

      <div>
        <Paragraph line={20} size={16} padding='none'>
          {formatPrice(promotion3MPrice)} {currency}{' '}
          {currency === PLN && t('registration.flow.credit.card.step.gross')}
        </Paragraph>

        <Paragraph
          line={20}
          size={14}
          padding='none'
          decoration='line-through'
          color='accent-3'
        >
          {formatPrice(price)} {currency}{' '}
          {currency === PLN && t('registration.flow.credit.card.step.gross')}
        </Paragraph>
      </div>
    </div>
  )
}

export const TotalDueTodayFreePromotion12M = ({
  price,
  priceInMonths,
  currency,
  billingPeriod,
  planRecordKey
}: {
  price: number
  priceInMonths: number
  currency: CurrencyType
  billingPeriod: BillingPeriodType
  planRecordKey: string
}) => {
  const { t } = useTranslation()

  const promotion12MPrice =
    promotion12MDiscountedPricesBrutto[
      planRecordKey as (typeof PROMOTION_12M_PLANS)[number]
    ][currency]

  const nettoPriceInMonths =
    currency === PLN ? convertPriceToNetto(priceInMonths) : priceInMonths

  const nettoPriceInMonthsPromotion =
    currency === PLN
      ? convertPriceToNetto(promotion12MPrice)
      : promotion12MPrice

  const equationPromotion = getPriceEquation({
    t,
    priceInMonths: formatPrice(nettoPriceInMonthsPromotion),
    oldPriceInMonths: formatPrice(nettoPriceInMonths),
    currency,
    billingPeriod
  })

  return (
    <div className={styles['summary-subscription-modal__row']}>
      <div className={styles['summary-subscription-modal__icon']}>
        <Icon icon='icon-credit-card-outline' size={14} />
      </div>
      <div>
        <Paragraph weight={700} line={20} size={16} padding='none'>
          <Trans i18nKey='change.plan.page.total.due.today' />
        </Paragraph>
      </div>

      <Column>
        <Row>
          <Paragraph
            className={styles['price-in-months-paragraph']}
            line={20}
            size={14}
            padding='none'
            decoration='line-through'
            color='accent-3'
          >
            {formatPrice(price)} {currency}
          </Paragraph>

          <Spreader spread={10} />

          <Paragraph
            className={styles['price-in-months-paragraph']}
            line={20}
            size={16}
            padding='none'
          >
            {formatPrice(promotion12MPrice * 12)} {currency}{' '}
            {currency === PLN && t('registration.flow.credit.card.step.gross')}
          </Paragraph>
        </Row>

        <Paragraph line={20} size={12} padding='none' color='color-8'>
          {equationPromotion}
        </Paragraph>
      </Column>
    </div>
  )
}

export const TotalDueTodayPromotion12M = ({
  price,
  priceInMonths,
  currency,
  billingPeriod,
  planRecordKey,
  date
}: {
  price: number
  priceInMonths: number
  currency: CurrencyType
  billingPeriod: BillingPeriodType
  planRecordKey: string
  date?: string
}) => {
  const { t } = useTranslation()

  const promotion12MPrice =
    promotion12MDiscountedPricesBrutto[
      planRecordKey as (typeof PROMOTION_12M_PLANS)[number]
    ][currency]

  const nettoPriceInMonths =
    currency === PLN ? convertPriceToNetto(priceInMonths) : priceInMonths

  const nettoPriceInMonthsPromotion =
    currency === PLN
      ? convertPriceToNetto(promotion12MPrice)
      : promotion12MPrice

  const equationPromotion = getPriceEquation({
    t,
    priceInMonths: formatPrice(nettoPriceInMonthsPromotion),
    oldPriceInMonths: formatPrice(nettoPriceInMonths),
    currency,
    billingPeriod
  })

  return (
    <Fragment>
      <div className={styles['summary-subscription-modal__row']}>
        <div className={styles['summary-subscription-modal__icon']}>
          <Icon icon='icon-credit-card-outline' size={14} />
        </div>
        <div>
          <Paragraph weight={700} line={20} size={16} padding='none'>
            <Trans i18nKey='change.plan.page.total.due.today' />
          </Paragraph>
        </div>

        <div>
          <Paragraph
            weight={700}
            line={20}
            size={16}
            padding='none'
            color='success'
          >
            0 {currency}
          </Paragraph>
        </div>
      </div>

      <Spacer space='tiny' />

      <div className={styles['summary-subscription-modal__row-billing']}>
        <Paragraph line={20} size={14} padding='none' color='color-8'>
          {date
            ? t('total.due.on.date', {
                date: formatDateUTCToDisplay(date, { withTime: false })
              })
            : null}
        </Paragraph>

        <Column>
          <Row>
            <Paragraph
              className={styles['price-in-months-paragraph']}
              line={20}
              size={14}
              padding='none'
              decoration='line-through'
              color='accent-3'
            >
              {formatPrice(price)} {currency}
            </Paragraph>

            <Spreader spread={10} />

            <Paragraph line={20} size={16} padding='none'>
              {formatPrice(promotion12MPrice * 12)} {currency}{' '}
              {currency === PLN &&
                t('registration.flow.credit.card.step.gross')}
            </Paragraph>
          </Row>

          <Paragraph line={20} size={12} padding='none' color='color-8'>
            {equationPromotion}
          </Paragraph>
        </Column>
      </div>
    </Fragment>
  )
}

export const TotalDueTodayFreePromotionForFreePlans = ({
  price,
  currency,
  planRecordKey
}: {
  price: number
  currency: CurrencyType
  planRecordKey: string
}) => {
  const { t } = useTranslation()

  const promotionForFreePlansPrice =
    promotionForFreePlanDiscountedPricesBrutto[
      planRecordKey as (typeof PROMOTION_FOR_FREE_PLAN_PLANS)[number]
    ][currency]

  return (
    <div className={styles['summary-subscription-modal__row']}>
      <div className={styles['summary-subscription-modal__icon']}>
        <Icon icon='icon-credit-card-outline' size={14} />
      </div>
      <div>
        <Paragraph weight={700} line={20} size={16} padding='none'>
          <Trans i18nKey='change.plan.page.total.due.today' />
        </Paragraph>
      </div>

      <div>
        <Paragraph line={20} size={16} padding='none'>
          {formatPrice(promotionForFreePlansPrice)} {currency}{' '}
          {currency === PLN && t('registration.flow.credit.card.step.gross')}
        </Paragraph>

        <Paragraph
          line={20}
          size={14}
          padding='none'
          decoration='line-through'
          color='accent-3'
        >
          {formatPrice(price)} {currency}{' '}
          {currency === PLN && t('registration.flow.credit.card.step.gross')}
        </Paragraph>
      </div>
    </div>
  )
}

export const TotalDueToday = ({
  price,
  priceInMonths,
  currency,
  billingPeriod,
  discount,
  date
}: {
  price: number
  priceInMonths: number
  currency: CurrencyType
  billingPeriod: BillingPeriodType
  discount?: Discount | null
  date?: string
}) => {
  const { t } = useTranslation()
  const nettoPriceInMonths =
    currency === PLN ? convertPriceToNetto(priceInMonths) : priceInMonths

  const equation = getPriceEquation({
    t,
    priceInMonths: formatPrice(nettoPriceInMonths),
    currency,
    billingPeriod,
    discount
  })

  return (
    <Fragment>
      <div className={styles['summary-subscription-modal__row']}>
        <div className={styles['summary-subscription-modal__icon']}>
          <Icon icon='icon-credit-card-outline' size={14} />
        </div>
        <div>
          <Paragraph weight={700} line={20} size={16} padding='none'>
            <Trans i18nKey='change.plan.page.total.due.today' />
          </Paragraph>
        </div>

        <div>
          <Paragraph
            weight={700}
            line={20}
            size={16}
            padding='none'
            color='success'
          >
            0 {currency}
          </Paragraph>
        </div>
      </div>

      <Spacer space='tiny' />

      <div className={styles['summary-subscription-modal__row-billing']}>
        <Paragraph line={20} size={14} padding='none' color='color-8'>
          {date
            ? t('total.due.on.date', {
                date: formatDateUTCToDisplay(date, { withTime: false })
              })
            : null}
        </Paragraph>

        <div>
          <Paragraph line={20} size={14} padding='none' color='color-8'>
            {formatPrice(price)} {currency}{' '}
            {currency === PLN && t('registration.flow.credit.card.step.gross')}
          </Paragraph>

          <Paragraph line={20} size={12} padding='none' color='color-8'>
            {equation}
          </Paragraph>
        </div>
      </div>
    </Fragment>
  )
}
